// Email API - API integration for email communications
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';
import type { Email, StructuredRecipient } from '../types';
import { formatTenantContactName } from '../utils/formatters';

// Backend response type (snake_case from API)
interface BackendEmail {
  id: string;
  user_id: string;
  direction: 'incoming' | 'outgoing';
  status: string;
  from_email: string;
  from_name: string;
  to_emails: Array<{ name: string; address: string; tenantContactId?: string }>;
  cc_emails?: Array<{ name: string; address: string; tenantContactId?: string }>;
  bcc_emails?: Array<{ name: string; address: string; tenantContactId?: string }>;
  subject?: string;
  preview?: string;
  body?: any; // Lexical JSON (JSONB from backend)
  html_body?: string; // Raw HTML body (for quote emails, etc.)
  is_read: boolean;
  is_starred: boolean;
  sent_at?: string;
  opened_at?: string;
  open_count?: number;
  track_opens?: boolean;
  created_at: string;
  updated_at: string;
  provider_thread_id?: string;
  contact_name?: string;
  contact_email?: string;
  tenant_company_contact_id?: string;
  template_id?: string | null;
  in_reply_to?: string | null;
  // Multi-recipient support (from email_contacts table)
  recipient_count?: number;
  email_contacts?: Array<{
    id: string;
    tenant_contact_id: string | null;
    email_address: string;
    display_name: string | null;
    recipient_type: 'to' | 'cc' | 'bcc' | 'from';
    // Nested tenant contact for current name (source of truth)
    tenantContact?: {
      id: string;
      first_name: string;
      last_name: string;
    } | null;
  }>;
  attachments?: Array<{
    id: string;
    email_id: string;
    file_name: string;
    file_url: string;
    file_type: string;
    file_size?: number;
    content_id?: string;
    is_inline: boolean;
    created_at: string;
    updated_at: string;
  }>;
}

// What the send endpoint returns when the provider accepted the email but saving our copy failed.
interface UnsavedSentEmail {
  id: string;
  persistenceFailed: true;
}

/**
 * The email was delivered, but Commander could not save its copy. Not a send failure:
 * callers must not offer a retry, which would deliver it twice.
 */
export class EmailSentNotSavedError extends Error {
  constructor(readonly emailId: string) {
    super(
      "Email sent, but we couldn't save a copy in Commander. Check your Sent folder before resending."
    );
    this.name = 'EmailSentNotSavedError';
  }
}

// Request/response types
export interface EmailListParams {
  page?: number;
  limit?: number;
  direction?: 'incoming' | 'outgoing';
  status?: 'draft' | 'scheduled' | 'sent' | 'delivered' | 'bounced' | 'failed';
  status_in?: Array<'draft' | 'scheduled' | 'sent' | 'delivered' | 'bounced' | 'failed'>;
  is_read?: boolean;
  is_starred?: boolean;
  tenant_company_contact_id?: string;
  provider?: string;
}

/**
 * Get first contact name with priority: current tenant contact → display_name → fallbacks
 */
function getFirstContactName(backendEmail: BackendEmail): string {
  const firstContact = backendEmail.email_contacts?.[0];

  if (firstContact?.tenantContact) {
    const name = formatTenantContactName(firstContact.tenantContact);
    if (name) return name;
  }

  return (
    firstContact?.display_name || backendEmail.contact_name || backendEmail.to_emails[0]?.name || ''
  );
}

/**
 * Transform backend email response to frontend Email type
 */
function transformEmail(backendEmail: BackendEmail): Email {
  // Get first contact name from email_contacts if available (multi-recipient support)
  // The badge will show the count, so we only display the first recipient name
  // Priority: tenantContact (current name) → display_name (snapshot) → contact_name → to_emails
  const firstContactName =
    (backendEmail.email_contacts?.length ?? 0) > 0
      ? getFirstContactName(backendEmail)
      : backendEmail.contact_name || backendEmail.to_emails[0]?.name || '';

  const firstContactEmail =
    backendEmail.email_contacts && backendEmail.email_contacts.length > 0
      ? backendEmail.email_contacts[0]?.email_address ||
        backendEmail.contact_email ||
        backendEmail.to_emails[0]?.address ||
        ''
      : backendEmail.contact_email || backendEmail.to_emails[0]?.address || '';

  return {
    id: backendEmail.id,
    type: 'email' as const,
    direction: backendEmail.direction,
    contactId: backendEmail.tenant_company_contact_id || '',
    contactName: firstContactName,
    contactEmail: firstContactEmail,
    subject: backendEmail.subject || '(No Subject)',
    content: backendEmail.preview || '', // Plain text preview
    timestamp: new Date(backendEmail.sent_at || backendEmail.created_at),
    status: backendEmail.status as any,
    from: backendEmail.from_email,
    to: backendEmail.to_emails.map((e) => ({
      address: e.address,
      name: e.name || undefined,
      tenantContactId: e.tenantContactId,
    })),
    cc: backendEmail.cc_emails?.map((e) => ({
      address: e.address,
      name: e.name || undefined,
      tenantContactId: e.tenantContactId,
    })),
    bcc: backendEmail.bcc_emails?.map((e) => ({
      address: e.address,
      name: e.name || undefined,
      tenantContactId: e.tenantContactId,
    })),
    priority: 'normal' as const,
    isRead: backendEmail.is_read,
    isStarred: backendEmail.is_starred,
    sentAt: backendEmail.sent_at ? new Date(backendEmail.sent_at) : undefined,
    openedAt: backendEmail.opened_at ? new Date(backendEmail.opened_at) : undefined,
    openCount: backendEmail.open_count || 0,
    trackOpens: backendEmail.track_opens,
    threadId: backendEmail.provider_thread_id,
    templateId: backendEmail.template_id || undefined,
    inReplyTo: backendEmail.in_reply_to || undefined,
    // Use html_body for raw HTML emails (quotes), otherwise undefined
    html: backendEmail.html_body || undefined,
    text: backendEmail.preview, // Plain text preview
    // Only set lexicalBody if body is an object (Lexical JSON), not for raw HTML emails
    lexicalBody:
      typeof backendEmail.body === 'object' && backendEmail.body !== null
        ? backendEmail.body
        : undefined,
    // Multi-recipient support
    recipientCount: backendEmail.recipient_count,
    recipientContactIds: backendEmail.email_contacts
      ?.filter((ec) => ec.tenant_contact_id)
      .map((ec) => ec.tenant_contact_id) as string[] | undefined,
    metadata: {
      tags: [],
      // Transform attachments (filter out inline attachments - they're embedded in Lexical body)
      attachments:
        backendEmail.attachments
          ?.filter((att) => !att.is_inline) // Only non-inline attachments
          .map((att) => ({
            id: att.id,
            name: att.file_name,
            size: att.file_size || 0,
            type: att.file_type,
            url: att.file_url,
            uploadedAt: new Date(att.created_at),
          })) || [],
    },
    createdBy: backendEmail.user_id,
    createdAt: new Date(backendEmail.created_at),
    updatedAt: new Date(backendEmail.updated_at),
  };
}

/**
 * Why a sent thread's auto follow-up toggle is unavailable. The first group hides the toggle
 * (the Compose switch would not have been offered on that email); the rest show it disabled.
 */
export type ThreadAutoFollowUpReason =
  | 'NO_OUTBOUND_SEND'
  | 'NOT_SINGLE_RECIPIENT'
  | 'NOT_MANUAL_SEND'
  | 'NO_ASSIGNED_PROSPECT'
  | 'PROSPECT_NOT_ELIGIBLE'
  | 'NO_AUTOPILOT_ACCESS'
  | 'REPLIED'
  | 'MANAGED_BY_AUTOPILOT'
  | 'OTHER_SEQUENCE';

/** GET/PUT /communications/emails/:emailId/auto-follow-up */
export interface ThreadAutoFollowUp {
  eligible: boolean;
  enabled: boolean;
  reason: ThreadAutoFollowUpReason | null;
  sequenceId: string | null;
  nextScheduledAt: string | null;
  triggerEmailId: string | null;
}

export interface EmailSendData {
  to: (string | StructuredRecipient)[];
  cc?: (string | StructuredRecipient)[];
  bcc?: (string | StructuredRecipient)[];
  subject?: string;
  body: {
    html?: string;
    lexical?: any;
  };
  contactId?: string;
  attachmentIds?: string[];
  templateId?: string;
  signatureId?: string;
  trackOpens?: boolean;
  /** Per-send toggle: enroll this first email to a prospect into a follow-up sequence */
  enrollInSequence?: boolean;
  replyToEmailId?: string;
  isDraft?: boolean;
  sourceDraftId?: string;
  quoteId?: string;
  quoteConfirmData?: {
    auto_create_payment_link?: boolean;
    bank_account_id?: string;
    attachmentId?: string;
  };
}

export interface EmailUpdateData {
  to?: (string | StructuredRecipient)[];
  cc?: (string | StructuredRecipient)[];
  bcc?: (string | StructuredRecipient)[];
  subject?: string;
  body?: {
    html?: string;
    lexical?: any;
  };
  contactId?: string;
  attachmentIds?: string[];
  templateId?: string;
  signatureId?: string;
  trackOpens?: boolean;
  replyToEmailId?: string;
}

/**
 * Factory function to create email API service with ApiClient dependency injection
 * @param apiClient - ApiClient instance from context
 * @returns Service object with all email API methods
 *
 * @example
 * ```ts
 * import { useApiClient } from '@commander/api-client';
 * import { createEmailApi } from './services/emailApi';
 *
 * const apiClient = useApiClient();
 * const emailApi = createEmailApi(apiClient);
 * const emails = await emailApi.getEmails({ page: 1, limit: 20 });
 * ```
 */
export const createEmailApi = (apiClient: ApiClient) => ({
  /**
   * Get paginated list of emails with filtering
   * GET /communications/emails
   */
  async getEmails(params?: EmailListParams) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.direction) queryParams.append('direction', params.direction);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.status_in && params.status_in.length > 0) {
      for (const status of params.status_in) {
        queryParams.append('status_in', status);
      }
    }
    if (params?.is_read !== undefined) queryParams.append('is_read', params.is_read.toString());
    if (params?.is_starred !== undefined)
      queryParams.append('is_starred', params.is_starred.toString());
    if (params?.tenant_company_contact_id)
      queryParams.append('tenant_company_contact_id', params.tenant_company_contact_id);
    if (params?.provider) queryParams.append('provider', params.provider);

    const endpoint = `/communications/emails${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      data: BackendEmail[];
      meta: {
        current_page: number;
        from: number;
        last_page: number;
        per_page: number;
        to: number;
        total: number;
      };
    }>(endpoint);

    return {
      emails: response.data.map(transformEmail),
      pagination: response.meta,
    };
  },

  /**
   * Get email thread (all emails in the same conversation)
   * GET /communications/emails/:emailId
   */
  async getEmailThread(emailId: string, params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const endpoint = `/communications/emails/${emailId}${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      data: BackendEmail[];
      meta: {
        current_page: number;
        from: number;
        last_page: number;
        per_page: number;
        to: number;
        total: number;
      };
    }>(endpoint);

    return {
      emails: response.data.map(transformEmail),
      pagination: response.meta,
    };
  },

  /**
   * Get single email with full details (including body)
   * GET /communications/emails/:emailId/details
   */
  async getEmailDetails(emailId: string) {
    const response = await apiClient.get<{ data: BackendEmail }>(
      `/communications/emails/${emailId}/details`
    );
    return transformEmail(response.data);
  },

  /**
   * Send or draft an email
   * POST /communications/emails
   */
  async sendEmail(data: EmailSendData) {
    const response = await apiClient.post<{ data: BackendEmail | UnsavedSentEmail }>(
      '/communications/emails',
      data
    );
    if ('persistenceFailed' in response.data) {
      throw new EmailSentNotSavedError(response.data.id);
    }
    return transformEmail(response.data);
  },

  /**
   * Update a draft email
   * PUT /communications/emails/:emailId
   */
  async updateDraftEmail(emailId: string, data: EmailUpdateData) {
    const response = await apiClient.put<{ data: BackendEmail }>(
      `/communications/emails/${emailId}`,
      data
    );
    return transformEmail(response.data);
  },

  /**
   * Star or unstar an email
   * PATCH /communications/emails/:id/star
   */
  async starEmail(emailId: string, isStarred: boolean) {
    const response = await apiClient.patch<{ data: BackendEmail }>(
      `/communications/emails/${emailId}/star`,
      { isStarred }
    );
    return transformEmail(response.data);
  },

  /**
   * Mark email as read or unread
   * PATCH /communications/emails/:id/read
   */
  async markEmailRead(emailId: string, isRead: boolean) {
    const response = await apiClient.patch<{ data: BackendEmail }>(
      `/communications/emails/${emailId}/read`,
      { isRead }
    );
    return transformEmail(response.data);
  },

  /**
   * Auto follow-up state of the sent thread an email belongs to
   * GET /communications/emails/:emailId/auto-follow-up
   */
  async getThreadAutoFollowUp(emailId: string): Promise<ThreadAutoFollowUp> {
    const response = await apiClient.get<{ data: ThreadAutoFollowUp }>(
      `/communications/emails/${emailId}/auto-follow-up`
    );
    return response.data;
  },

  /**
   * Turn auto follow-ups on or off for the sent thread an email belongs to
   * PUT /communications/emails/:emailId/auto-follow-up
   */
  async setThreadAutoFollowUp(emailId: string, enabled: boolean): Promise<ThreadAutoFollowUp> {
    const response = await apiClient.put<{ data: ThreadAutoFollowUp }>(
      `/communications/emails/${emailId}/auto-follow-up`,
      { enabled }
    );
    return response.data;
  },

  /**
   * Delete an email (soft delete)
   * DELETE /communications/emails/:emailId
   */
  async deleteEmail(emailId: string): Promise<void> {
    await apiClient.delete<void>(`/communications/emails/${emailId}`);
  },

  /**
   * Get download URL for email attachment
   * GET /communications/email_attachments/:attachmentId/download
   * @param attachmentId - The ID of the attachment to download
   * @param expiresInMinutes - Number of minutes until the URL expires (default: 60)
   * @param disposition - Whether to display inline or as attachment (default: 'inline')
   */
  async downloadAttachment(
    attachmentId: string,
    expiresInMinutes = 60,
    disposition: 'inline' | 'attachment' = 'inline'
  ) {
    const queryParams = new URLSearchParams();
    queryParams.append('expiresInMinutes', expiresInMinutes.toString());
    queryParams.append('disposition', disposition);

    const response = await apiClient.get<{
      url: string;
      expiresAt: string | null;
      isPublic: boolean;
    }>(`/communications/email_attachments/${attachmentId}/download?${queryParams}`);

    return response;
  },
});

// Type for the service instance
export type EmailApi = ReturnType<typeof createEmailApi>;
